import Alert from '@/components/Alert/Alert'
import { Box } from '@/components/ui/box'
import { Divider } from '@/components/ui/divider'
import { HStack } from '@/components/ui/hstack'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { selectTableCoverById } from '@/redux/slices/tableCoverSlice'
import { useAppSelector } from '@/redux/store'
import { BaseFileEnum, type FolderCoverFragment, type FormCoverFragment } from '@/typings/domain'
import dayjs from 'dayjs'
import { Link } from 'expo-router'
import { Fragment } from 'react'
import FolderIcon from '../covers/FolderIcon'
import TableIcon from './TableIcon'

type TableCoverProps = {
  id: string
  showBorder: boolean
}

export default function TableCover({ id, showBorder }: TableCoverProps) {
  const cover = useAppSelector(state => selectTableCoverById(state.tableCover, id))

  if (!cover) return null

  return cover.type === BaseFileEnum.Form ? (
    <TableItem table={cover as FormCoverFragment} showBorder={showBorder} />
  ) : (
    <FolderItem folder={cover as FolderCoverFragment} />
  )
}

type TableItemProps = {
  table: FormCoverFragment
  showBorder: boolean
}

function TableItem({ table, showBorder }: TableItemProps) {
  const handleDisabledPress = () => {
    Alert.alert('表格已被封停')
  }

  if (table.disabled) {
    return (
      <Pressable className="opacity-50" onPress={handleDisabledPress}>
        <TableItemContent
          name={table.subname || table.name}
          descriptionItems={[`${dayjs(table.createdAt).format('YYYY-MM-DD')} 创建`, `${table.entriesCount} 条数据`]}
          showBorder={showBorder}
        />
      </Pressable>
    )
  }

  return (
    <Link href={`/tables/${table.token}`} asChild>
      <Pressable className="data-[active=true]:bg-background-100">
        <TableItemContent
          name={table.subname || table.name}
          descriptionItems={[`${dayjs(table.createdAt).format('YYYY-MM-DD')} 创建`, `${table.entriesCount} 条数据`]}
          showBorder={showBorder}
        />
      </Pressable>
    </Link>
  )
}

type FolderItemProps = {
  folder: FolderCoverFragment
}

function FolderItem({ folder }: FolderItemProps) {
  return (
    <Box className="px-6 py-2">
      <Link href={`/table_folders/${folder.token}`} asChild>
        <Pressable className="rounded-lg bg-background-50 data-[active=true]:bg-background-100">
          <HStack space="lg" className="p-4 items-center">
            <FolderIcon color={folder.ensuredSymbol?.backgroundColor} />
            <Text size="body" className="text-label flex-1 min-w-0">
              {folder.name}
            </Text>
          </HStack>
        </Pressable>
      </Link>
    </Box>
  )
}

type TableItemContentProps = {
  name: string
  descriptionItems: string[]
  showBorder: boolean
}

function TableItemContent({ name, descriptionItems, showBorder }: TableItemContentProps) {
  return (
    <HStack className="items-justify px-2">
      <Box className="p-4">
        <TableIcon />
      </Box>
      <VStack
        space="sm"
        className={`flex-1 justify-center py-2 mr-4 ${showBorder ? 'border-b border-background-100' : ''}`}
      >
        <Text size="body" className="text-label">
          {name}
        </Text>
        {descriptionItems.length > 0 && (
          <HStack space="sm" className="items-center">
            {descriptionItems.map((desc, index) => (
              <Fragment key={index}>
                {index > 0 && <Divider orientation="vertical" className="h-3/5" />}
                <Text size="subhead" className="text-secondary-label">
                  {desc}
                </Text>
              </Fragment>
            ))}
          </HStack>
        )}
      </VStack>
    </HStack>
  )
}
